import React, { useEffect, useState } from "react";
import CustomTable from "../../../shared/custom-table/custom-table";
import { columnsRequests } from "../../../mocks/fakeData";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Box,
  Grid,
  Button,
  Modal,
} from "@mui/material";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faExternalLinkAlt, 
  faFileAlt, 
  faTimes,
  faCheck,
  faCalendarAlt,
  faLayerGroup,
  faSave,
  faCheckCircle
} from "@fortawesome/free-solid-svg-icons";
import {
  getAllTeacherRequests,
  respondOfferTeacherService,
  adjustTeacherOfferRequestService,
} from "../../../services/teacher-offer";

interface RequestData {
  id: number;
  nom: string; // Changé pour garantir que nom existe toujours
  offerType: string;
  status: "PENDING" | "ACCEPTED" | "REJECTED";
  createdAt: string;
  startDate: string | null;
  endDate: string | null;
  allowedClassNumber?: number;
  teacherOffer?: any;
  paymentImageUrl: string;
}

type TableData = Record<string, any>;

const Requests = () => {
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [selectedRow, setSelectedRow] = useState<RequestData | null>(null);
  const [data, setData] = useState<RequestData[]>([]);
  const [originalData, setOriginalData] = useState<RequestData[]>([]);
  const [selectedStatus, setSelectedStatus] = useState<"ACCEPTED" | "REJECTED">("ACCEPTED");
  const [confirmAction, setConfirmAction] = useState("");
  const [loading, setLoading] = useState(true);
  const [openAdjustDialog, setOpenAdjustDialog] = useState(false);
  const [adjustPayload, setAdjustPayload] = useState<{ allowedClassNumber: string; startDate: string; endDate: string }>({
    allowedClassNumber: "",
    startDate: "",
    endDate: "",
  });
  const [savingAdjust, setSavingAdjust] = useState(false);
  const [openImageModal, setOpenImageModal] = useState(false);
  const [currentImageUrl, setCurrentImageUrl] = useState("");
  
  const [searchName, setSearchName] = useState("");
  const [selectedStatusFilter, setSelectedStatusFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState<"asc" | "desc" | "none">("none");

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await getAllTeacherRequests();
      
      console.log("Données reçues de l'API:", res.data); // Debug
      
      const formattedData = res.data.map((item: any) => ({
        ...item,
        id: item.id,
        nom: item.superTeacherName || "Inconnu",
        offerType: item.teacherOffer?.title || "Type inconnu",
        status: item.status,
        createdAt: item.requestDate,
        startDate: item.startDate || null,
        endDate: item.endDate || null,
        allowedClassNumber: item.allowedClassNumber ?? item.teacherOffer?.classNumber ?? null,
        paymentImageUrl: item.paymentImageUrl || "#",
      }));
      
      console.log("Données formatées:", formattedData); // Debug
      setData(formattedData);
      setOriginalData(formattedData);
    } catch (e) {
      console.error("Error fetching data:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (originalData.length === 0) return;

    let filteredData = [...originalData];

    // Filtre par nom
    if (searchName.trim()) {
      const searchTerm = searchName.toLowerCase().trim();
      filteredData = filteredData.filter(item => {
        const nom = item.nom.toLowerCase();
        return nom.includes(searchTerm);
      });
    }

    // Filtre par statut
    if (selectedStatusFilter !== "all") {
      filteredData = filteredData.filter(item => item.status === selectedStatusFilter);
    }

    // Tri par date
    if (dateFilter !== "none") {
      filteredData.sort((a, b) => {
        const dateA = new Date(a.createdAt);
        const dateB = new Date(b.createdAt);
        return dateFilter === "asc" 
          ? dateA.getTime() - dateB.getTime() 
          : dateB.getTime() - dateA.getTime();
      });
    }

    setData(filteredData);
  }, [searchName, selectedStatusFilter, dateFilter, originalData]);

  const handleResetFilters = () => {
    setSearchName("");
    setSelectedStatusFilter("all");
    setDateFilter("none");
    setData(originalData);
  };

  const handleActionClick = (
    row: RequestData,
    action: string,
    status: "ACCEPTED" | "REJECTED"
  ) => {
    setSelectedRow(row);
    setConfirmAction(action);
    setOpenConfirmDialog(true);
    setSelectedStatus(status);
  };

  const handleCloseConfirmDialog = () => {
    setOpenConfirmDialog(false);
    setSelectedRow(null);
  };

  const handleOpenAdjustDialog = (row: RequestData) => {
    setSelectedRow(row);
    const todayStr = new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
      .toISOString()
      .slice(0, 10);
    setAdjustPayload({
      allowedClassNumber: row.allowedClassNumber != null
        ? String(row.allowedClassNumber)
        : row.teacherOffer?.classNumber != null
          ? String(row.teacherOffer.classNumber)
          : "",
      startDate: row.startDate ? String(row.startDate).slice(0, 10) : todayStr,
      endDate: row.endDate ? String(row.endDate).slice(0, 10) : "",
    });
    setOpenAdjustDialog(true);
  };

  const handleCloseAdjustDialog = () => {
    setOpenAdjustDialog(false);
    setSelectedRow(null);
    setAdjustPayload({ allowedClassNumber: "", startDate: "", endDate: "" });
  };

  const handleOpenImageModal = (url: string) => {
    setCurrentImageUrl(url);
    setOpenImageModal(true);
  };

  const handleCloseImageModal = () => {
    setOpenImageModal(false);
    setCurrentImageUrl("");
  };

  const handleActionConfirm = async () => {
    if (!selectedRow) return;
    
    try {
      await respondOfferTeacherService(selectedRow.id, selectedStatus);
      await fetchData();
      setOpenConfirmDialog(false);
      setSelectedRow(null);
    } catch (e) {
      console.error("Error responding to offer:", e);
    }
  };

  const handleAdjustConfirm = async () => {
    if (!selectedRow) return;
    const allowedValue = Number(adjustPayload.allowedClassNumber);
    if (!Number.isFinite(allowedValue) || allowedValue <= 0) {
      alert("Veuillez saisir un nombre de groupes valide.");
      return;
    }
    if (!adjustPayload.startDate || !adjustPayload.endDate) {
      alert("Veuillez renseigner les dates de debut et de fin.");
      return;
    }
    if (new Date(adjustPayload.endDate) < new Date(adjustPayload.startDate)) {
      alert("La date de fin doit etre posterieure a la date de debut.");
      return;
    }
    try {
      setSavingAdjust(true);
      await adjustTeacherOfferRequestService(selectedRow.id, {
        allowedClassNumber: allowedValue,
        startDate: adjustPayload.startDate,
        endDate: adjustPayload.endDate,
        status: "ACCEPTED",
      });
      await fetchData();
      handleCloseAdjustDialog();
    } catch (e) {
      console.error("Error updating request:", e);
    } finally {
      setSavingAdjust(false);
    }
  };

  const renderActions = (row: TableData) => {
    const requestRow = row as RequestData;
    return (
      <div className="flex items-center justify-center space-x-2">
        {requestRow.status === "PENDING" && (
          <>
            <button
              className="px-3 py-1 text-white rounded-full bg-primary_bg"
              onClick={() => handleActionClick(requestRow, "Accepted", "ACCEPTED")}
            >
              Accept
            </button>
            <button
              onClick={() => handleActionClick(requestRow, "Rejected", "REJECTED")}
              className="px-3 py-1 text-white rounded-full bg-red"
            >
              Reject
            </button>
          </>
        )}
        {requestRow.status === "ACCEPTED" && (
          <button
            className="px-3 py-1 text-white rounded-full bg-primary"
            onClick={() => handleOpenAdjustDialog(requestRow)}
          >
            Ajuster
          </button>
        )}
        {requestRow.status === "REJECTED" && (
          <span className="text-gray-500">
            Rejected
          </span>
        )}
      </div>
    );
  };

  const renderStatus = (row: TableData) => {
    const requestRow = row as RequestData;
    return (
      <div className="flex items-center justify-center space-x-2">
        <button
          className="px-3 py-1 text-white rounded-full bg-text cursor-pointer"
          onClick={() => handleOpenImageModal(requestRow.paymentImageUrl)}
        >
          Voir le Doc
        </button>
      </div>
    );
  };

  const handleRefresh = async () => {
    setLoading(true);
    await fetchData();
  };

  return (
    <div className="w-full min-h-screen bg-[#F8FAFB] p-4 lg:p-10 animate-in fade-in duration-700">
      
      {/* Header Section */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-10">
        <div>
           <h1 className="text-3xl md:text-4xl font-black text-title tracking-tight uppercase">
             Demandes <span className="text-primary italic">Professeurs</span>
           </h1>
           <p className="text-[11px] font-bold text-text/40 uppercase tracking-[0.4em] mt-2 ml-1">
             Validation des offres enseignants
           </p>
        </div>
        
        <button
          onClick={handleRefresh}
          disabled={loading}
          className="flex items-center gap-3 px-6 py-4 bg-white text-title font-black text-[11px] uppercase tracking-widest rounded-2xl shadow-sm border border-gray-100 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 disabled:opacity-50 group"
        >
          <div className={`w-2 h-2 rounded-full bg-primary ${loading ? 'animate-ping' : ''}`}></div>
          <span>Actualiser la liste</span>
        </button>
      </div>

      {/* Stats Quick View */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-10">
          {[
            { label: 'En attente', count: data.filter(d => d.status === 'PENDING').length, color: 'text-amber-500', bg: 'bg-amber-50' },
            { label: 'Acceptées', count: data.filter(d => d.status === 'ACCEPTED').length, color: 'text-emerald-500', bg: 'bg-emerald-50' },
            { label: 'Rejetées', count: data.filter(d => d.status === 'REJECTED').length, color: 'text-rose-500', bg: 'bg-rose-50' },
            { label: 'Total', count: data.length, color: 'text-blue-500', bg: 'bg-blue-50' },
          ].map((stat, i) => (
            <div key={i} className="bg-white/80 backdrop-blur-md p-5 rounded-3xl border border-white shadow-sm flex items-center justify-between group hover:shadow-lg transition-all">
                <div>
                   <p className="text-[10px] font-black text-text/40 uppercase tracking-widest mb-1">{stat.label}</p>
                   <p className={`text-2xl font-black ${stat.color}`}>{stat.count}</p>
                </div>
                <div className={`w-12 h-12 ${stat.bg} rounded-2xl flex items-center justify-center`}>
                    <div className={`w-2 h-2 rounded-full ${stat.color.replace('text', 'bg')} ${stat.label === 'En attente' ? 'animate-bounce' : ''}`}></div>
                </div>
            </div>
          ))}
      </div>

      {/* Filters Section */}
      <div className="bg-white/80 backdrop-blur-xl p-8 rounded-[2.5rem] border border-white shadow-xl mb-10">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="space-y-2">
            <label className="text-[10px] font-black text-title uppercase tracking-widest ml-2">Chercher un prof</label>
            <TextField
              fullWidth
              placeholder="Nom de l'enseignant..."
              size="small"
              value={searchName}
              onChange={(e) => setSearchName(e.target.value)}
              InputProps={{
                sx: { borderRadius: '1.25rem', bgcolor: '#F9FAFB', '& fieldset': { border: 'none' }, height: '54px', fontWeight: 800, fontSize: '13px' }
              }}
            />
          </div>

          <div className="space-y-2">
            <label className="text-[10px] font-black text-title uppercase tracking-widest ml-2">Status</label>
            <FormControl fullWidth size="small">
              <Select
                value={selectedStatusFilter}
                onChange={(e) => setSelectedStatusFilter(e.target.value as string)}
                sx={{ borderRadius: '1.25rem', bgcolor: '#F9FAFB', '& fieldset': { border: 'none' }, height: '54px', fontWeight: 800, fontSize: '13px' }}
              >
                <MenuItem value="all">Tous</MenuItem>
                <MenuItem value="PENDING">En attente</MenuItem>
                <MenuItem value="ACCEPTED">Accepté</MenuItem>
                <MenuItem value="REJECTED">Rejeté</MenuItem>
              </Select>
            </FormControl>
          </div>

          <div className="space-y-2">
            <label className="text-[10px] font-black text-title uppercase tracking-widest ml-2">Date</label>
            <FormControl fullWidth size="small">
              <Select
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value as "asc" | "desc" | "none")}
                sx={{ borderRadius: '1.25rem', bgcolor: '#F9FAFB', '& fieldset': { border: 'none' }, height: '54px', fontWeight: 800, fontSize: '13px' }}
              >
                <MenuItem value="none">Aucun</MenuItem>
                <MenuItem value="asc">Ancien</MenuItem>
                <MenuItem value="desc">Récent</MenuItem>
              </Select>
            </FormControl>
          </div>

          <div className="flex items-end">
            <button
              onClick={handleResetFilters}
              className="w-full h-[54px] bg-primary/10 text-primary font-black text-[10px] uppercase tracking-widest rounded-2xl hover:bg-primary hover:text-white transition-all duration-500"
            >
              Réinitialiser
            </button>
          </div>
        </div>
      </div>

      {/* Main Content Table Area */}
      <div className="bg-white/40 backdrop-blur-md rounded-[3rem] border border-white shadow-2xl overflow-hidden min-h-[400px]">
        {loading ? (
          <div className="flex flex-col items-center justify-center h-[500px] space-y-4">
             <div className="w-16 h-16 border-4 border-primary/20 border-t-primary rounded-full animate-spin"></div>
             <p className="text-[10px] font-black text-text/40 uppercase tracking-widest text-center">Recherche des demandes...</p>
          </div>
        ) : data.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-[500px] text-center px-10">
             <div className="w-24 h-24 bg-gray-50 rounded-[2rem] flex items-center justify-center mb-6 text-gray-200 text-4xl">📎</div>
             <h3 className="text-xl font-black text-title uppercase mb-2">Aucune demande prof</h3>
             <p className="text-sm text-text/50 max-w-md">La liste est actuellement vide.</p>
          </div>
        ) : (
          <div className="p-4 md:p-8">
              <CustomTable
                title="Tableau des Demandes"
                columns={[
                  {
                    Header: "Professeur",
                    accessor: "nom",
                    Cell: ({ value }: any) => {
                      const colors = [
                        'bg-blue-500/10 text-blue-600', 
                        'bg-emerald-500/10 text-emerald-600', 
                        'bg-purple-500/10 text-purple-600', 
                        'bg-amber-500/10 text-amber-600', 
                        'bg-rose-500/10 text-rose-600'
                      ];
                      const colorIndex = (value?.length || 0) % colors.length;
                      const colorClass = colors[colorIndex];
                      
                      return (
                        <div className="flex items-center gap-4">
                          <div className={`w-12 h-12 rounded-2xl ${colorClass} flex items-center justify-center text-sm font-black border border-white shadow-sm shrink-0`}>
                            {value ? value.substring(0, 2).toUpperCase() : 'PR'}
                          </div>
                          <div className="flex flex-col">
                            <span className="font-black text-title text-[14px] uppercase tracking-tighter leading-none">{value}</span>
                            <span className="text-[9px] font-bold text-text/30 uppercase tracking-[0.2em] mt-1.5">Enseignant Certifié</span>
                          </div>
                        </div>
                      )
                    }
                  },
                  {
                    Header: "Offre Demandée",
                    accessor: "offerType",
                    Cell: ({ value }: any) => (
                      <div className="flex flex-col">
                        <span className="text-[11px] font-black text-[#05322B] uppercase tracking-wider bg-emerald-50 px-4 py-2 rounded-xl border border-emerald-100/50 whitespace-nowrap inline-block text-center">
                          {value}
                        </span>
                      </div>
                    )
                  },
                  {
                    Header: "Date Demande",
                    accessor: "createdAt",
                    Cell: ({ value }: any) => {
                      if (!value) return <span className="text-text/20">—</span>;
                      const date = new Date(value);
                      const day = date.getDate().toString().padStart(2, '0');
                      const months = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'];
                      const month = months[date.getMonth()];
                      return (
                        <div className="flex flex-col">
                           <span className="text-[13px] font-black text-title tracking-tight">{day} {month} {date.getFullYear()}</span>
                           <span className="text-[9px] font-bold text-text/20 uppercase tracking-widest mt-1">Date d'enregistrement</span>
                        </div>
                      );
                    }
                  },
                  {
                    Header: "Groupes",
                    accessor: "allowedClassNumber",
                    Cell: ({ value }: any) => (
                      <div className="flex items-center gap-3">
                         <div className="w-10 h-10 rounded-xl bg-slate-50 flex items-center justify-center border border-gray-100">
                            <span className="text-[15px] font-black text-title">{value || 0}</span>
                         </div>
                         <span className="text-[9px] font-black text-text/30 uppercase tracking-[0.4em]">Unités</span>
                      </div>
                    )
                  },
                  {
                    Header: "Statut",
                    accessor: "status",
                    Cell: ({ value }: any) => {
                      const configs: any = {
                        'PENDING': { label: 'En attente', color: 'text-amber-600', bg: 'bg-amber-50', dot: 'bg-amber-500', glow: 'shadow-amber-500/20' },
                        'ACCEPTED': { label: 'Validé', color: 'text-emerald-600', bg: 'bg-emerald-50', dot: 'bg-emerald-500', glow: 'shadow-emerald-500/20' },
                        'REJECTED': { label: 'Refusé', color: 'text-rose-600', bg: 'bg-rose-50', dot: 'bg-rose-500', glow: 'shadow-rose-500/20' }
                      };
                      const config = configs[value] || configs['PENDING'];
                      return (
                        <div className={`px-5 py-2 ${config.bg} ${config.color} rounded-full inline-flex items-center gap-3 border border-white shadow-sm`}>
                           <div className={`w-2 h-2 rounded-full ${config.dot} ${config.glow} shadow-lg ${value === 'PENDING' ? 'animate-pulse' : ''}`}></div>
                           <span className="text-[10px] font-black uppercase tracking-[0.2em] whitespace-nowrap">{config.label}</span>
                        </div>
                      );
                    }
                  }
                ]}
                data={data}
                 actions={(row: any) => {
                  const req = row as RequestData;
                  if (req.status === 'PENDING') {
                    return (
                      <div className="flex items-center justify-center gap-3">
                        <button 
                          onClick={() => handleActionClick(req, "Accepter", "ACCEPTED")}
                          className="h-11 px-6 bg-[#05322B] text-white text-[10px] font-black uppercase tracking-widest rounded-2xl shadow-xl hover:shadow-emerald-500/20 hover:-translate-y-1 transition-all active:scale-95 outline-none"
                        >
                          Confirmer
                        </button>
                        <button 
                          onClick={() => handleActionClick(req, "Refuser", "REJECTED")}
                          className="h-11 px-6 bg-slate-50 text-rose-500 text-[10px] font-black uppercase tracking-widest rounded-2xl border border-rose-100 hover:bg-rose-500 hover:text-white transition-all active:scale-95 outline-none"
                        >
                          Rejeter
                        </button>
                      </div>
                    );
                  }
                  if (req.status === 'ACCEPTED') {
                    return (
                      <div className="px-2">
                        <button 
                          onClick={() => handleOpenAdjustDialog(req)}
                          className="h-12 w-full bg-[#05322B] text-white text-[11px] font-black uppercase tracking-[0.2em] rounded-[1.25rem] shadow-xl hover:shadow-emerald-500/10 hover:-translate-y-1 transition-all active:scale-95 outline-none block text-center leading-[3rem]"
                        >
                          Ajuster Groupes
                        </button>
                      </div>
                    );
                  }
                  return <span className="text-[10px] font-black text-text/20 uppercase tracking-[0.4em] italic text-center block w-full">Finalisé</span>;
                }}
                status={(row: any) => {
                  const req = row as RequestData;
                  return (
                    <div className="flex justify-center">
                      <button 
                        onClick={() => handleOpenImageModal(req.paymentImageUrl)}
                        className="group flex flex-col items-center gap-2 p-2 hover:bg-slate-50 rounded-2xl transition-all cursor-pointer outline-none"
                      >
                        <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center text-text/30 group-hover:bg-[#05322B] group-hover:text-white transition-all shadow-sm border border-gray-100 group-hover:scale-110 duration-500">
                           <FontAwesomeIcon icon={faFileAlt} className="text-base" />
                        </div>
                        <span className="text-[9px] font-black text-text/40 uppercase tracking-[0.2em] group-hover:text-[#05322B]">Preuve</span>
                      </button>
                    </div>
                  );
                }}
              />
          </div>
        )}
      </div>

      {/* Modal Ajuster l'offre */}
      <Modal 
        open={openAdjustDialog} 
        onClose={handleCloseAdjustDialog}
        className="flex items-center justify-center p-4 backdrop-blur-xl bg-title/40"
      >
        <motion.div 
          className="w-full max-w-[550px] bg-white rounded-[3rem] p-8 lg:p-12 shadow-3xl overflow-hidden outline-none relative"
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 20 }}
        >
          {/* Header */}
          <div className="flex items-center gap-5 mb-10 pb-6 border-b border-gray-50">
             <div className="w-14 h-14 rounded-2xl bg-[#05322B] text-emerald-400 flex items-center justify-center text-2xl shadow-xl shadow-emerald-900/10">
                <FontAwesomeIcon icon={faLayerGroup} />
             </div>
             <div>
                <h3 className="text-xl font-black text-title uppercase tracking-tighter leading-none">Configuration <span className="text-primary">Offre</span></h3>
                <p className="text-[10px] text-text/40 font-bold uppercase tracking-[0.3em] mt-2 italic">Ajustement des paramètres d'accès</p>
             </div>
          </div>

          <div className="space-y-8">
             {/* Nombre de groupes */}
             <div className="space-y-3">
                <label className="text-[10px] font-black text-title uppercase tracking-widest ml-1 flex items-center gap-2">
                   <FontAwesomeIcon icon={faLayerGroup} className="text-[9px] text-primary" />
                   Nombre de groupes autorisés
                </label>
                <input 
                  type="number" 
                  className="w-full h-14 bg-slate-50 rounded-2xl px-6 font-black text-title text-base border-2 border-transparent focus:border-primary/20 focus:bg-white transition-all outline-none"
                  value={adjustPayload.allowedClassNumber}
                  onChange={(e) => setAdjustPayload(prev => ({ ...prev, allowedClassNumber: e.target.value }))}
                />
             </div>

             <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Date Début */}
                <div className="space-y-3">
                   <label className="text-[10px] font-black text-title uppercase tracking-widest ml-1 flex items-center gap-2">
                      <FontAwesomeIcon icon={faCalendarAlt} className="text-[9px] text-primary" />
                      Date de début
                   </label>
                   <input 
                     type="date" 
                     className="w-full h-14 bg-slate-50 rounded-2xl px-6 font-black text-title text-xs uppercase border-2 border-transparent focus:border-primary/20 focus:bg-white transition-all outline-none"
                     value={adjustPayload.startDate}
                     onChange={(e) => setAdjustPayload(prev => ({ ...prev, startDate: e.target.value }))}
                   />
                </div>
                {/* Date Fin */}
                <div className="space-y-3">
                   <label className="text-[10px] font-black text-title uppercase tracking-widest ml-1 flex items-center gap-2">
                      <FontAwesomeIcon icon={faCalendarAlt} className="text-[9px] text-primary" />
                      Date de fin
                   </label>
                   <input 
                     type="date" 
                     className="w-full h-14 bg-slate-50 rounded-2xl px-6 font-black text-title text-xs uppercase border-2 border-transparent focus:border-primary/20 focus:bg-white transition-all outline-none"
                     value={adjustPayload.endDate}
                     onChange={(e) => setAdjustPayload(prev => ({ ...prev, endDate: e.target.value }))}
                   />
                </div>
             </div>
          </div>

          {/* Actions */}
          <div className="mt-12 flex gap-4">
             <button
               className="flex-1 h-16 bg-slate-50 text-text/40 font-black text-[11px] uppercase tracking-[0.2em] rounded-2xl hover:bg-rose-500 hover:text-white transition-all duration-500 active:scale-95 outline-none"
               onClick={handleCloseAdjustDialog}
               disabled={savingAdjust}
             >
               Annuler
             </button>
             <button
               className="flex-[2] h-16 bg-[#05322B] text-white font-black text-[11px] uppercase tracking-[0.2em] rounded-2xl shadow-[0_20px_40px_rgba(5,50,43,0.15)] hover:shadow-[0_25px_50px_rgba(16,185,129,0.25)] hover:-translate-y-1 transition-all duration-500 active:scale-95 flex items-center justify-center gap-3 outline-none"
               onClick={handleAdjustConfirm}
               disabled={savingAdjust}
             >
               {savingAdjust ? (
                 <div className="w-5 h-5 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
               ) : (
                 <>
                   <FontAwesomeIcon icon={faSave} />
                   SAUVEGARDER
                 </>
               )}
             </button>
          </div>
        </motion.div>
      </Modal>


      {/* Modal Confirmation Action */}
      <Modal 
        open={openConfirmDialog} 
        onClose={handleCloseConfirmDialog}
        className="flex items-center justify-center p-4 backdrop-blur-xl bg-title/40"
      >
        <motion.div 
          className="w-full max-w-[450px] bg-white rounded-[3rem] p-10 shadow-3xl text-center outline-none"
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
        >
           <div className="w-20 h-20 rounded-[2rem] bg-emerald-50 text-emerald-500 flex items-center justify-center text-3xl mx-auto mb-6 border border-emerald-100 shadow-sm relative">
              <div className="absolute inset-0 bg-emerald-400/20 rounded-[2rem] animate-ping"></div>
              <FontAwesomeIcon icon={faCheckCircle} className="relative z-10" />
           </div>
           
           <h3 className="text-2xl font-black text-title uppercase tracking-tighter mb-4">Confirmer l'Action</h3>
           <p className="text-sm font-medium text-text/60 leading-relaxed mb-10">
              Êtes-vous sûr de vouloir <span className="text-[#05322B] font-black">{confirmAction.toLowerCase()}</span> la demande de <span className="text-[#05322B] font-black">{selectedRow?.nom || "cet utilisateur"}</span> ?
           </p>

           <div className="flex gap-4">
              <button
                className="flex-1 h-14 bg-slate-50 text-text/40 font-black text-[10px] uppercase tracking-widest rounded-xl hover:bg-rose-500 hover:text-white transition-all duration-500 outline-none"
                onClick={handleCloseConfirmDialog}
              >
                Annuler
              </button>
              <button
                className="flex-1 h-14 bg-[#05322B] text-white font-black text-[10px] uppercase tracking-widest rounded-xl shadow-xl hover:shadow-emerald-500/20 hover:-translate-y-1 transition-all duration-500 outline-none"
                onClick={handleActionConfirm}
              >
                {confirmAction}
              </button>
           </div>
        </motion.div>
      </Modal>

      {/* Justification Image Lightbox Modal */}
      <Modal
        open={openImageModal}
        onClose={handleCloseImageModal}
        className="flex items-center justify-center p-4 backdrop-blur-xl bg-title/40"
        closeAfterTransition
      >
        <motion.div 
          className="relative max-w-5xl w-full bg-white rounded-[3rem] p-4 lg:p-8 shadow-3xl overflow-hidden outline-none"
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
        >
           <div className="flex items-center justify-between mb-6 px-4">
              <div className="flex items-center gap-4">
                 <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-500 flex items-center justify-center">
                    <FontAwesomeIcon icon={faFileAlt} />
                 </div>
                 <div>
                    <h3 className="text-lg font-black text-title uppercase tracking-tighter leading-none">Justificatif de Paiement</h3>
                    <p className="text-[10px] text-text/40 font-bold uppercase tracking-widest mt-1">Validation Professeur</p>
                 </div>
              </div>
              <button 
                onClick={handleCloseImageModal}
                className="w-11 h-11 rounded-full bg-slate-50 flex items-center justify-center text-text/40 hover:text-rose-500 hover:rotate-90 transition-all duration-500 cursor-pointer outline-none"
              >
                 <FontAwesomeIcon icon={faTimes} />
              </button>
           </div>

           <div className="bg-slate-50 rounded-[2rem] overflow-hidden flex items-center justify-center border-4 border-slate-50 group">
              <img 
                src={currentImageUrl} 
                alt="Payment Justification" 
                className="max-h-[70vh] w-auto object-contain shadow-2xl transition-transform duration-700 group-hover:scale-105"
              />
           </div>

           <div className="mt-6 flex justify-end px-4">
              <a 
                href={currentImageUrl} 
                target="_blank" 
                rel="noreferrer"
                className="h-12 px-6 bg-title text-white font-black text-[10px] uppercase tracking-widest rounded-xl hover:bg-primary transition-all flex items-center gap-2"
              >
                 <FontAwesomeIcon icon={faExternalLinkAlt} />
                 Ouvrir l'original
              </a>
           </div>
        </motion.div>
      </Modal>
    </div>
  );
};

export default Requests;
